import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, CardView, ProgressBar, rem, SCREEN_BOTTOM_PADDING, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import Amount from 'design-system/Amount';
import BlurAmountView from 'design-system/BlurAmountView';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import CardRow from 'design-system/CardRow';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { capitalizeFirstCharacter, formatAmount } from 'utils';
import { formatDate } from 'date-fns';
import useColors from 'hooks/useColors';
import Squircle from 'design-system/Squircle';
import IconCheck from 'design-system/icons/IconCheck';
import IconBlock from 'design-system/icons/IconBlock';
import Status from 'design-system/Status';
import { isWeb } from '../../../constants';
import CreditReportAddressMatchItem from '../components/CreditReportAddressMatchItem';
import CreditScorePaymentHistoryChart from '../components/CreditScorePaymentHistoryChart';
import CreditReportLenderLogo from '../components/CreditReportLenderLogo';
import { selectCreditReportAccountById, selectCreditReportTrackableDebts, selectShouldShowAccountConsolidateDebtCard, } from '../selectors';
import useCreditReportDetailItem from '../hooks/useCreditReportDetailItem';
import { prepareAccountNumber } from '../utils';
import ConnectSuggestedBankCard from '../components/ConnectSuggestedBankCard';
import DisputeActionCard from '../components/DisputeActionCard';
import RecurringPaymentsItem from '../components/RecurringPaymentsItem';
import CreditReportFooter from '../components/CreditReportFooter';
import { CreditReportConsolidateDebtCard, CreditReportTrackDebtsCard, } from '../components/CreditReportOfferCards';
const strings = {
    sections: {
        paymentHistory: 'Payment history',
        personalInformation: 'Personal information',
    },
    rows: {
        accountType: 'Account type',
        accountNumber: 'Account number',
        dateOpened: 'Date opened',
        dateClosed: 'Date closed',
        lastReported: 'Last reported',
        paymentFrequency: 'Payment frequency',
        balance: 'Balance',
        limit: 'Limit',
        startingBalance: 'Starting balance',
        trackInNetWorth: 'Track in net worth',
        active: 'Active',
    },
};
export const AccountBalanceCard = React.memo(({ limit, balance, currency, startingBalance, displayMode = 'full', customLabel, }) => {
    const colors = useColors();
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    if (displayMode === 'hidden' || balance === null || !currency)
        return null;
    const isRangeMode = displayMode === 'range' || displayMode === 'range-compact';
    const denominator = 
    // eslint-disable-next-line no-nested-ternary
    limit && limit > 0
        ? limit
        : startingBalance && startingBalance > 0
            ? startingBalance
            : null;
    if (isRangeMode && denominator) {
        const rawPercentUsed = (balance / denominator) * 100;
        const percentUsed = Math.max(0, Math.min(100, Math.round(rawPercentUsed)));
        const progressColors = [
            colors.elements.brand.toString(),
            colors.elements.brand.toString(),
        ];
        return (<CardView noMarginTop noMarginBottom>
          <Spacer h={4}/>
          <CardRow small tableSpacing title={<BlurAmountView enabled={scrambled}>
                <Text TextThin-14 Gray>
                  {customLabel?.balance ?? `${percentUsed}% used`}
                </Text>
              </BlurAmountView>} info={<BlurAmountView enabled={scrambled}>
                <Text Numbers-14>
                  {formatAmount(balance, currency)}
                  {' / '}
                  {formatAmount(denominator, currency)}
                </Text>
              </BlurAmountView>}/>
          {displayMode === 'range' ? (<ProgressBar style={styles.progressContainer} position={percentUsed} colors={progressColors}/>) : (<Spacer h={4}/>)}
        </CardView>);
    }
    return (<CardView noMarginTop noMarginBottom>
        <Spacer h={4}/>
        <CardRow small tableSpacing title={<BlurAmountView enabled={!!(scrambled && customLabel?.balance)}>
              <Text TextThin-14 Gray>
                {customLabel?.balance ?? strings.rows.balance}
              </Text>
            </BlurAmountView>} info={<Amount Numbers-14 showFraction={false} value={{ value: balance, currency }}/>}/>
        {!isRangeMode && limit !== null && (<CardRow small tableSpacing title={<BlurAmountView enabled={!!(scrambled && customLabel?.limit)}>
                <Text TextThin-14 Gray>
                  {customLabel?.limit ?? strings.rows.limit}
                </Text>
              </BlurAmountView>} info={<Amount Numbers-14 showFraction={false} value={{ value: limit, currency }}/>}/>)}
        {displayMode === 'full' && startingBalance !== null && (<CardRow small tableSpacing title={<BlurAmountView enabled={!!(scrambled && customLabel?.startingBalance)}>
                <Text TextThin-14 Gray>
                  {customLabel?.startingBalance ?? strings.rows.startingBalance}
                </Text>
              </BlurAmountView>} info={<Amount Numbers-14 showFraction={false} value={{ value: startingBalance, currency }}/>}/>)}
        <Spacer h={4}/>
      </CardView>);
});
const CreditReportAccountDetailScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation, route }) => {
    const accountId = Number(route.params.accountId);
    const goToParent = useCallback(() => navigation.navigate('CreditReportAccountsScreen'), [navigation]);
    const account = useCreditReportDetailItem(Number.isFinite(accountId), (state) => selectCreditReportAccountById(state, accountId), goToParent);
    const showConsolidateDebt = useAppSelector((state) => selectShouldShowAccountConsolidateDebtCard(state, accountId));
    const trackableDebts = useAppSelector(selectCreditReportTrackableDebts);
    const trackableDebt = useMemo(() => trackableDebts.find((debt) => debt.accountId === accountId), [accountId, trackableDebts]);
    const isTrackDebtCardDismissed = useAppSelector((state) => trackableDebt?.networthAccountId != null &&
        !!state.deviceSettings.dismissedTrackCreditReportDebtsIds?.includes(trackableDebt.networthAccountId));
    const colors = useColors();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [navigation]);
    if (!account) {
        return <AppView withFrame={false}/>;
    }
    const { isClosed, limit, balance, iconUrl, lenderName, displayMode, customLabel, accountNumber, statusHistory, currencyCode: currency, updatedDate: lastReported, accountEndDate: dateClosed, accountTypeName: accountType, accountStartDate: dateOpened, openingBalance: startingBalance, repaymentFrequency: paymentFrequency, } = account;
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle}>
            <>
              <View style={styles.headerContainer}>
                <CreditReportLenderLogo size="large" onDark iconUrl={iconUrl} name={lenderName}/>
                <View style={styles.headerTextContainer}>
                  <Text Text-20 centered>
                    {lenderName}
                  </Text>
                  <View style={styles.statusContainer}>
                    {isClosed ? (<IconBlock width={rem(16)} height={rem(16)} color={colors.elements.statusOffline}/>) : (<Squircle color={colors.elements.statusPositive} width={rem(16)} height={rem(16)}>
                        <View style={styles.checkIcon}>
                          <IconCheck width={rem(12)} height={rem(12)} thick color={colors.cards.onDark}/>
                        </View>
                      </Squircle>)}
                    <Text TextThin-14 {...(isClosed ? { Secondary: true } : { Positive: true })}>
                      {isClosed ? 'Closed' : 'Currently open'}
                    </Text>
                  </View>
                </View>
              </View>
              {showConsolidateDebt ? (<CreditReportConsolidateDebtCard source="accountDetail" style={styles.consolidateDebtCard}/>) : null}

              <ConnectSuggestedBankCard tuAccountId={account.accountId} routeKey={route.key}/>

              <View style={styles.cardsContainer}>
                <CardView noMarginTop noMarginBottom>
                  <Spacer h={4}/>
                  {!!accountType && (<CardRow small tableSpacing title={strings.rows.accountType} info={accountType}/>)}
                  {!!accountNumber && (<CardRow small tableSpacing title={strings.rows.accountNumber} info={prepareAccountNumber(accountNumber)}/>)}
                  {!!dateOpened && (<CardRow small tableSpacing title={strings.rows.dateOpened} info={formatDate(dateOpened, 'd MMM yyyy')}/>)}

                  {!!dateClosed && (<CardRow small tableSpacing title={strings.rows.dateClosed} info={formatDate(dateClosed, 'd MMM yyyy')}/>)}

                  {!!lastReported && (<CardRow small tableSpacing title={strings.rows.lastReported} info={formatDate(lastReported, 'd MMM yyyy')}/>)}

                  {!!paymentFrequency && (<CardRow small tableSpacing title={strings.rows.paymentFrequency} info={capitalizeFirstCharacter(paymentFrequency)}/>)}
                  <Spacer h={4}/>
                </CardView>
                <AccountBalanceCard balance={balance} limit={limit} currency={currency} startingBalance={startingBalance} displayMode={displayMode} customLabel={customLabel}/>
                {trackableDebt?.isTrackedInNetworth ? (<CardView noMarginTop noMarginBottom>
                    <CardRow small tableSpacing title={strings.rows.trackInNetWorth} info={<Status active activeText={strings.rows.active} inactiveText={strings.rows.active}/>}/>
                  </CardView>) : null}
              </View>
              {trackableDebt &&
            !trackableDebt.isTrackedInNetworth &&
            !isTrackDebtCardDismissed ? (<CreditReportTrackDebtsCard source="accountDetail" withMarginTop/>) : null}
              <CreditScorePaymentHistoryChart statusHistory={statusHistory}/>
              <RecurringPaymentsItem account={account}/>
              <CreditReportAddressMatchItem accountHolderAddress={account.accountHolderAddress}/>
              <DisputeActionCard params={{
            category: 'account',
            accountId: account.accountId ?? undefined,
        }}/>
              <CreditReportFooter />
            </>
          </Animated.ScrollView>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditReportAccountDetailScreen;
const styles = StyleSheet.create({
    headerContainer: {
        alignItems: 'center',
        gap: rem(16),
        marginVertical: rem(8),
    },
    headerTextContainer: {
        gap: rem(4),
        justifyContent: 'center',
        marginHorizontal: rem(16),
    },
    statusContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: rem(4),
    },
    checkIcon: {
        marginTop: rem(2),
    },
    cardsContainer: {
        gap: rem(16),
        marginTop: rem(16),
    },
    consolidateDebtCard: {
        marginTop: rem(16),
        marginBottom: 0,
    },
    progressContainer: {
        paddingHorizontal: rem(16),
        marginBottom: rem(20),
    },
});
